import { currentChapter } from '@neopolis/engine';
import type {
  ChainRole,
  Questionnaire,
  ClassificationVerdict,
  ConnectedUsefulness,
  Content,
  GameState,
  ReglageProgramme,
  Step,
} from '@neopolis/engine';

import { T } from '../textes-interface.js';
import type { EtatDeSynchronisation } from '../persistence/syncing-adapter.js';
import { BarreJauges } from './BarreJauges.js';
import { IndicateurDeSauvegarde } from './IndicateurDeSauvegarde.js';
import { QuestionnaireDeFinDeChapitre } from './QuestionnaireDeFinDeChapitre.js';
import { BoutonTexteAgrandi } from './BoutonTexteAgrandi.js';
import { FilAriane } from './FilAriane.js';
import { MessageDuMaire } from './MessageDuMaire.js';
import { EtapeAnalyser } from './chapitre1/EtapeAnalyser.js';
import { EtapeClasser } from './chapitre1/EtapeClasser.js';
import { EtapeDecider } from './chapitre1/EtapeDecider.js';
import { EtapeExplorer } from './chapitre1/EtapeExplorer.js';
import { RapportDiagnostic } from './chapitre1/RapportDiagnostic.js';
import { EtapeAmeliorer } from './chapitre2/EtapeAmeliorer.js';
import { EtapeComprendre } from './chapitre2/EtapeComprendre.js';
import { EtapeConcevoir } from './chapitre2/EtapeConcevoir.js';
import { EtapeTester } from './chapitre2/EtapeTester.js';
import { EtapeReparer } from './chapitre3/EtapeReparer.js';
import { EtapeSemaine } from './chapitre3/EtapeSemaine.js';
import { FicheArrosage } from './chapitre3/FicheArrosage.js';
import { EtapeBilanTournees } from './chapitre4/EtapeBilanTournees.js';
import { EtapeOrganiser } from './chapitre4/EtapeOrganiser.js';
import { FicheCollecte } from './chapitre4/FicheCollecte.js';
import { FicheTechnique } from './chapitre2/FicheTechnique.js';

export interface ActionsDePartie {
  onAllerEtape: (stepIndex: number) => void;
  onDecouvrir: (problemId: string) => void;
  onClasser: (problemId: string, categoryId: string) => void;
  onValiderClassement: () => void;
  onAnalyser: (problemId: string, reponse: ConnectedUsefulness) => void;
  onDefinirPriorites: (priorites: readonly string[]) => void;
  onEcrireTrace: (fieldId: string, valeur: string) => void;
  onReglerProgramme: (reglage: ReglageProgramme) => void;
  onOrganiserTournee: (jour: string, conteneurs: readonly string[]) => void;
  onOuvrirRapport: () => void;
  onFermerRapport: () => void;
  onRepondreQuestionGuidee: (questionId: string, choiceId: string) => void;
  onPoserEquipement: (slot: ChainRole, equipmentId: string) => void;
  onRetirerEquipement: (slot: ChainRole, equipmentId: string) => void;
  onDeployer: () => void;
  onResoudreEvenement: (eventId: string, choiceId: string | null) => void;
  onRevenirAConcevoir: () => void;
  onChapitreSuivant: () => void;
  onRepondreAuQuestionnaire: (reponses: Questionnaire) => void;
  onBasculerTexte: () => void;
  onQuitter: () => void;
}

/**
 * Choisit le composant d'une étape à partir de sa `mecanique`.
 *
 * C'est le contenu qui décide de quoi est faite une étape, pas son numéro : un
 * chapitre réordonné n'oblige à toucher à rien ici.
 */
function EtapeCourante({
  content,
  partie,
  etape,
  verdicts,
  messagesEvenement,
  debatClasse,
  actions,
}: {
  content: Content;
  partie: GameState;
  etape: Step;
  verdicts: readonly ClassificationVerdict[];
  messagesEvenement: readonly string[];
  debatClasse: string | null;
  actions: ActionsDePartie;
}) {
  switch (etape.mecanique) {
    case 'exploration_carte':
      return <EtapeExplorer content={content} partie={partie} onDecouvrir={actions.onDecouvrir} />;

    case 'tri_categories':
      return (
        <EtapeClasser
          content={content}
          partie={partie}
          verdicts={verdicts}
          onClasser={actions.onClasser}
          onValider={actions.onValiderClassement}
        />
      );

    case 'choix_oui_partiel_non':
      return <EtapeAnalyser content={content} partie={partie} onAnalyser={actions.onAnalyser} />;

    case 'priorisation_top3':
      return (
        <EtapeDecider
          content={content}
          partie={partie}
          onDefinirPriorites={actions.onDefinirPriorites}
          onJustifier={(texte) => actions.onEcrireTrace('justification', texte)}
        />
      );

    case 'observation_guidee':
      return (
        <EtapeComprendre
          content={content}
          partie={partie}
          onRepondre={actions.onRepondreQuestionGuidee}
        />
      );

    case 'glisser_deposer_chaine':
      return (
        <EtapeConcevoir
          content={content}
          partie={partie}
          onPoser={actions.onPoserEquipement}
          onRetirer={actions.onRetirerEquipement}
        />
      );

    case 'reparer_programme':
      return (
        <EtapeReparer content={content} partie={partie} onRegler={actions.onReglerProgramme} />
      );

    case 'simulation_semaine':
      return <EtapeSemaine content={content} partie={partie} />;

    case 'organiser_tournee':
      return (
        <EtapeOrganiser
          content={content}
          partie={partie}
          onOrganiser={actions.onOrganiserTournee}
        />
      );

    case 'bilan_tournees':
      return <EtapeBilanTournees content={content} partie={partie} />;

    case 'simulation_nuit':
      return <EtapeTester content={content} partie={partie} onDeployer={actions.onDeployer} />;

    case 'evenement':
      return (
        <EtapeAmeliorer
          content={content}
          partie={partie}
          messages={messagesEvenement}
          debat={debatClasse}
          onResoudre={actions.onResoudreEvenement}
          onRevenirConcevoir={actions.onRevenirAConcevoir}
        />
      );

    default:
      return null;
  }
}

/**
 * L'écran d'une partie : jauges en haut, fil d'Ariane, puis l'étape courante.
 */
export function PartieEcran({
  content,
  partie,
  etapeMaximale,
  texteAgrandi,
  verdicts,
  rapportOuvert,
  messagesEvenement,
  debatClasse,
  erreur,
  synchronisation,
  chapitresOuverts,
  actions,
}: {
  content: Content;
  partie: GameState;
  etapeMaximale: number;
  texteAgrandi: boolean;
  verdicts: readonly ClassificationVerdict[];
  rapportOuvert: boolean;
  messagesEvenement: readonly string[];
  debatClasse: string | null;
  erreur: string | null;
  synchronisation: EtatDeSynchronisation;
  chapitresOuverts: readonly string[] | null;
  actions: ActionsDePartie;
}) {
  const chapitre = currentChapter(partie, content);
  const etape = chapitre.deroule[partie.stepIndex];
  const derniereEtape = partie.stepIndex === chapitre.deroule.length - 1;

  return (
    <div className="min-h-screen bg-slate-50">
      <a
        href="#contenu-principal"
        className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-50 focus:rounded-lg focus:bg-white focus:px-4 focus:py-2 focus:shadow"
      >
        {T.accessibilite.contenuPrincipal}
      </a>

      <div className="print:hidden">
        <BarreJauges content={content} gauges={partie.gauges} />
      </div>

      <main id="contenu-principal" className="mx-auto max-w-5xl space-y-5 p-4">
        <header className="flex flex-wrap items-center justify-between gap-3 print:hidden">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">{chapitre.titre}</h1>
            {etape && <p className="text-slate-600">{etape.consigne}</p>}
          </div>

          <div className="flex items-center gap-3">
            <IndicateurDeSauvegarde etat={synchronisation} />
            <BoutonTexteAgrandi actif={texteAgrandi} onBasculer={actions.onBasculerTexte} />
            <button
              type="button"
              onClick={actions.onQuitter}
              className="min-h-touch rounded-xl bg-white px-4 font-semibold text-slate-700 shadow-sm hover:bg-slate-50"
            >
              {T.accessibilite.quitter}
            </button>
          </div>
        </header>

        <div className="print:hidden">
          <FilAriane
            etapes={chapitre.deroule}
            etapeCourante={partie.stepIndex}
            etapeMaximale={etapeMaximale}
            onAller={actions.onAllerEtape}
          />
        </div>

        {rapportOuvert ? (
          <div className="space-y-5">
            {partie.chapterId === 'ch1' ? (
              <RapportDiagnostic
                content={content}
                partie={partie}
                onEcrireTrace={actions.onEcrireTrace}
                onFermer={actions.onFermerRapport}
                onChapitreSuivant={actions.onChapitreSuivant}
                chapitreSuivantOuvert={
                  chapitresOuverts === null ? null : chapitresOuverts.includes('ch2')
                }
              />
            ) : partie.chapterId === 'ch4' ? (
              <FicheCollecte
                content={content}
                partie={partie}
                onEcrireTrace={actions.onEcrireTrace}
                onFermer={actions.onFermerRapport}
              />
            ) : partie.chapterId === 'ch3' ? (
              <FicheArrosage
                content={content}
                partie={partie}
                onEcrireTrace={actions.onEcrireTrace}
                onFermer={actions.onFermerRapport}
              />
            ) : (
              <FicheTechnique
                content={content}
                partie={partie}
                onEcrireTrace={actions.onEcrireTrace}
                onFermer={actions.onFermerRapport}
              />
            )}

            <QuestionnaireDeFinDeChapitre
              reponses={partie.questionnaires[partie.chapterId]}
              onRepondre={actions.onRepondreAuQuestionnaire}
            />
          </div>
        ) : (
          <div className="space-y-5">
            {partie.stepIndex === 0 && (
              <MessageDuMaire titre={chapitre.titre}>
                <p>{chapitre.situation_depart}</p>
              </MessageDuMaire>
            )}

            {erreur !== null && (
              <p role="alert" className="rounded-xl bg-rose-50 p-3 font-medium text-rose-800">
                {erreur}
              </p>
            )}

            {etape && (
              <EtapeCourante
                content={content}
                partie={partie}
                etape={etape}
                verdicts={verdicts}
                messagesEvenement={messagesEvenement}
                debatClasse={debatClasse}
                actions={actions}
              />
            )}

            {derniereEtape && (
              <button
                type="button"
                onClick={actions.onOuvrirRapport}
                className="min-h-touch w-full rounded-xl bg-emerald-600 px-6 font-semibold text-white hover:bg-emerald-700"
              >
                {partie.chapterId === 'ch1' ? T.rapport.voir : T.fiche.voir}
              </button>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
